import * as React from 'react';

import { ItemConfig, ButtonConfig, ItemFactory } from './AllCKEditorButtonsConfig';
import ResponsiveBar from '../../components/ResponsiveBar';
import IconTextButtonGroup from '../../custom-components/icon-text-button/IconTextButtonGroup';
import IconTextButton from '../../custom-components/icon-text-button/IconTextButton';
import { IconTextButtonGroupProps } from '../../custom-components/icon-text-button/IconTextButtonGroup';

interface ToolbarGroup {
    groupProps: IconTextButtonGroupProps;
    items: ItemConfig[];
}

type ToolbarItem = ToolbarGroup | ItemConfig;
export type ToolbarItems = ToolbarItem[];

interface CKEditorToolbarProps extends React.Props<CKEditorToolbar> {
    ckEditor: CKEditor;
    items: ToolbarItems;
}

interface CKEditorToolbarState {
    [key: string]: number;
}

function itemIsGroup(item: ToolbarItem): item is ToolbarGroup {
    return typeof (item as ToolbarGroup).groupProps !== 'undefined';
}
function itemIsButton(item: ToolbarItem): item is ButtonConfig {
    return typeof (item as ButtonConfig).Icon !== 'undefined';
}
function itemIsFactory(item: ToolbarItem): item is ItemFactory {
    return typeof item === 'function';
}

interface CommandListener {
    (this: {state: number}): void;
}

export default class CKEditorToolbar extends React.Component<CKEditorToolbarProps, CKEditorToolbarState> {

    private commands: string[];
    private commandListeners: {
        [key: string]: CommandListener
    };

    constructor(props: CKEditorToolbarProps) {
        super(props);
        this.state = {};
        this.commands = [];
        this.commandListeners = {};
    }

    componentDidMount() {
        const self = this;

        this.commands.forEach((command) => {
            // `this.state` is the new command state:
            // http://docs.ckeditor.com/#!/api/CKEDITOR.command-event-state
            this.commandListeners[command] = function() {
                const newState = {} as CKEditorToolbarState;
                newState[command] = this.state;
                self.setState(newState);
            } as CommandListener;

            const commandObj = this.props.ckEditor.getCommand(command);
            this.commandListeners[command].call(commandObj);
            commandObj.on('state', this.commandListeners[command]);
        });

        // See #BUG-8964 - Rerender the component whenever the ckeditor
        // selection changes. This is done to ensure the color picker is always
        // updated even if no ckeditor command was executed to trigger a rerender
        this.props.ckEditor.on('selectionChange', this.selectionChangeListener);
    }

    componentWillUnmount() {
        this.commands.forEach((command) => {
            this.props.ckEditor.getCommand(command).removeListener('state', this.commandListeners[command]);
        });

        this.props.ckEditor.removeListener('selectionChange', this.selectionChangeListener);
    }

    render() {
        return (
            <div>
                <ResponsiveBar>
                    {this.props.items.map(this.renderItem)}
                </ResponsiveBar>
            </div>
        );
    }

    private selectionChangeListener = () => {
        this.forceUpdate();
    }

    private onTouchTapButton = (event: React.SyntheticEvent<{}>, item: ButtonConfig) => {
        let command = item.command;
        let commandData = item.commandData;

        if (!commandData) {
            commandData = () => undefined;
        }
        this.props.ckEditor.execCommand(command, commandData(event));
    };

    private renderItem = (item: ToolbarItem, index: number): React.ReactElement<any> => {
        const styles = this.getStyles();

        if (itemIsGroup(item)) {
            return (
                <IconTextButtonGroup
                    key={index}
                    style={styles.itemGroup}
                    dataTestId={item.groupProps.dataTestId}
                    {...item.groupProps}
                >
                    {item.items.map(this.renderItem)}
                </IconTextButtonGroup>
            );
        } else if (itemIsButton(item)) {
            if (this.commands.indexOf(item.command) === -1) {
                this.commands.push(item.command);
            }
            let buttonCfg = item;
            return (
                <IconTextButton
                    Icon={<buttonCfg.Icon />}
                    AdditionalIcon={buttonCfg.AdditionalIcon}
                    text={buttonCfg.text}
                    onTouchTap={(event) => this.onTouchTapButton(event, buttonCfg)}
                    isPressed={this.state[buttonCfg.command] === CKEDITOR.TRISTATE_ON}
                    isDisabled={this.state[buttonCfg.command] === CKEDITOR.TRISTATE_DISABLED}
                    useAlternativeColors={true}
                    style={styles.item}
                    key={buttonCfg.command}
                    dataTestId={"text-command-" + buttonCfg.command}
                />
            );
        } else if (itemIsFactory(item)) {
            return item(this.props.ckEditor);
        } else {
            return (
                <span key={index} style={styles.item}>&hellip;</span>
            );
        }
    };

    private getStyles() {
        return {
            itemGroup: {
                display: 'inline-block',
                marginLeft: 10,
                marginRight: 10
            },
            item: {
                marginLeft: 2,
                marginRight: 2
            }
        };
    }

}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/custom-components/ckeditor/CKEditorToolbar.tsx
 **/